import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Ticket } from 'lucide-react';
import { formatCurrency, formatDuration } from '@/lib/i18n';
import { api } from '@/lib/api';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Field, Select } from '@/components/ui/field';
import { useApi } from '@/Hooks/useApi';
import { pruneParams } from '../lib/helpers';
import { useVisitPicker } from '../lib/visits';
import { meterFor, type ResourceRow, type SessionRow } from '../lib/sessions';
import { usePricingListQuery } from '@/Pages/Pricing/pricingListsApi';
import { pricingListRoute } from '@/Pages/Pricing/pricingRoutes';
import { isPricingRefusal, refusalOf, type Refusal } from '@/Pages/Pricing/lib/refusals';
import { useRefusalToast } from '@/Pages/Pricing/lib/useRefusal';
import { PricingRuleList } from '@/Pages/Pricing/components/PricingRuleList';
import { RefusalNotice } from '@/Pages/Pricing/components/RefusalNotice';
import { VisitPicker } from './VisitPicker';
import { ResourceFormDialog } from './ResourceFormDialog';

/** What `GET /resource-sessions/entitlement-preview` answers (P10-08). */
interface EntitlementPreview {
  entitlementId: string;
  packageName: string | null;
  remainingMinutes: number;
  minutesToDraw: number;
  expiresAt: string | null;
}

interface Common {
  open: boolean;
  currency: string;
  onClose: () => void;
  onDone: () => void;
}

/** §10.1 — start the clock. The rule may be pinned here so the guest is quoted
 *  the price they were promised even if the session crosses a peak boundary. */
export function StartSessionDialog({
  resource,
  open,
  currency,
  onClose,
  onDone,
}: Common & { resource: ResourceRow | null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toastRefusal = useRefusalToast();
  const [plannedMinutes, setPlannedMinutes] = useState('60');
  const [playerCount, setPlayerCount] = useState('1');
  const [pricingRuleId, setPricingRuleId] = useState('');
  const [busy, setBusy] = useState(false);
  /**
   * §10.2 — the start was refused because nothing prices this resource.
   *
   * Held on the dialog rather than thrown at a toast, because it is the one
   * failure here the operator can fix from where they are standing, and a toast
   * that has already faded cannot carry the button that fixes it.
   */
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [attaching, setAttaching] = useState(false);
  const picker = useVisitPicker(open);

  /**
   * The rates this session could be charged at: the rate card's, not the
   * resource's. A resource attached to no card has none — which is a different
   * state from a card with nothing on it, and the reason the API refuses the
   * start with two different codes.
   */
  const attachedList = resource?.pricingList ?? null;
  const listQuery = usePricingListQuery(attachedList?.id ?? '', {
    skip: !open || !attachedList,
  });
  const rules = useMemo(() => listQuery.data?.rules ?? [], [listQuery.data]);

  const minutes = Number(plannedMinutes);
  const plannedForApi = Number.isFinite(minutes) && minutes > 0 ? Math.round(minutes) : undefined;

  // P10-08 — a guest holding "PS5 120 minutes" must be shown that the package
  // will pay for this session, not silently charged for it a second time.
  const previewParams = useMemo(
    () =>
      pruneParams({
        resourceId: resource?.id,
        checkId: picker.checkId,
        plannedMinutes: plannedForApi,
      }),
    [resource?.id, picker.checkId, plannedForApi],
  );
  const preview = useApi<EntitlementPreview | null>(
    open && resource && picker.checkId ? '/resource-sessions/entitlement-preview' : null,
    previewParams,
  );
  // A guest with no redeemable grant gets a `null` body, which axios hands back
  // as an empty string — so check the shape, not just truthiness.
  const entitlement =
    preview.data && typeof preview.data === 'object' ? (preview.data as EntitlementPreview) : null;
  const billableAfterPackage = plannedForApi
    ? Math.max(0, plannedForApi - (entitlement?.minutesToDraw ?? 0))
    : null;

  const submit = async () => {
    if (!resource) return;
    setBusy(true);
    setRefusal(null);
    try {
      await api.post('/resource-sessions', {
        resourceId: resource.id,
        playerCount: Math.max(1, Number(playerCount) || 1),
        ...(plannedForApi ? { plannedMinutes: plannedForApi } : {}),
        ...(pricingRuleId ? { pricingRuleId } : {}),
        // The bill is what makes an entitlement redeemable at all: a grant is
        // held by the check that bought it, so a meter started against no check
        // can only ever be billed as time. The table is sent too, so a meter
        // whose visit reopens its bill still routes to that table's new one.
        ...(picker.tableId ? { tableId: picker.tableId } : {}),
        ...(picker.checkId ? { checkId: picker.checkId } : {}),
      });
      toast.success(t('spaces.resources.started'));
      onDone();
      onClose();
    } catch (error) {
      // The three pricing refusals name what is wrong AND where it is fixed, so
      // they stay on the dialog with the way in. Everything else is a toast — in
      // this client's own words, never the server's — because it is not
      // something this operator can act on from where they are standing.
      const failure = refusalOf(error);
      if (isPricingRefusal(failure)) setRefusal(failure);
      else toastRefusal(error, { resource: resource.name });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('spaces.resources.startTitle', { name: resource?.name ?? '' })}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void submit()} disabled={busy}>
            {t('resource.start')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* §10.2 — the refusal that stops a venue trading, with the way out of
            it, and the way out is not the same door three times:

              - no card attached      → the RESOURCE's form, because
                                        `pricingListId` is a field on the resource;
              - card with no rate on  → the CARD, to add or reactivate one;
              - rates but none now    → the CARD, to widen a window.

            One "Set up pricing" button for all three would send a manager
            hunting a rate card for a gap that is not there, or building a second
            card beside one that only needed a switch flipped. `refusalSentence`
            decides which, from `code` and the counts the API puts on the payload
            precisely so a client can choose without parsing English. */}
        {refusal && (
          <RefusalNotice
            refusal={refusal}
            names={{ resource: resource?.name }}
            onFix={(fix) => {
              if (fix.kind === 'attachList') {
                setAttaching(true);
                return;
              }
              // The card is a screen, not a dialog: leaving the start dialog up
              // over a different route would strand the operator behind a form
              // for a console they can no longer see.
              onClose();
              navigate(pricingListRoute(fix.listId));
            }}
          />
        )}

        <VisitPicker
          visits={picker.visits}
          sessionId={picker.sessionId}
          onChange={picker.setSessionId}
          check={picker.check}
          loading={picker.loading}
          hint={t('spaces.resources.visitHint')}
        />

        {picker.checkId && entitlement && (
          <div className="flex items-start gap-2 rounded-lg border border-accent-line bg-accent-soft p-3 text-sm">
            <Ticket className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden />
            <div className="space-y-1">
              <p className="font-medium text-accent-soft-fg">
                {t('spaces.resources.entitlement.willBeUsed', {
                  minutes: entitlement.minutesToDraw,
                  package: entitlement.packageName ?? t('spaces.resources.entitlement.aPackage'),
                })}
              </p>
              <p className="text-xs text-accent">
                {t('spaces.resources.entitlement.remaining', {
                  count: entitlement.remainingMinutes,
                })}
                {entitlement.expiresAt
                  ? ` · ${t('spaces.resources.entitlement.expires', {
                      date: new Date(entitlement.expiresAt).toLocaleString(),
                    })}`
                  : ''}
              </p>
              <p className="text-xs text-accent">
                {billableAfterPackage === null
                  ? t('spaces.resources.entitlement.openEnded')
                  : billableAfterPackage === 0
                    ? t('spaces.resources.entitlement.noCharge')
                    : t('spaces.resources.entitlement.partialCharge', {
                        count: billableAfterPackage,
                      })}
              </p>
            </div>
          </div>
        )}

        {picker.checkId && !preview.loading && !entitlement && (
          <p className="text-xs text-ink-muted">
            {t('spaces.resources.entitlement.none')}
          </p>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label={t('spaces.resources.plannedMinutes')} hint={t('spaces.resources.plannedHint')}>
            <Input
              type="number"
              min={0}
              value={plannedMinutes}
              onChange={(event) => setPlannedMinutes(event.currentTarget.value)}
            />
          </Field>
          <Field label={t('spaces.resources.playerCount')}>
            <Input
              type="number"
              min={1}
              max={resource?.capacity ?? 64}
              value={playerCount}
              onChange={(event) => setPlayerCount(event.currentTarget.value)}
            />
          </Field>
        </div>

        <Field label={t('spaces.resources.pricingRule')}>
          <Select
            value={pricingRuleId}
            onChange={(event) => setPricingRuleId(event.currentTarget.value)}
          >
            <option value="">{t('spaces.resources.autoRule')}</option>
            {rules
              .filter((rule) => rule.isActive)
              .map((rule) => (
                <option key={rule.id} value={rule.id}>
                  {rule.name}
                </option>
              ))}
          </Select>
        </Field>

        <div>
          <p className="mb-1 text-xs font-medium">{t('spaces.resources.pricing')}</p>
          <PricingRuleList
            rules={rules}
            currency={currency}
            activeRuleId={pricingRuleId || null}
            emptyAction={
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  onClose();
                  navigate(pricingListRoute(attachedList?.id ?? null));
                }}
              >
                {attachedList
                  ? t('spaces.pricingRules.addFirst')
                  : t('spaces.pricingRules.manage')}
              </Button>
            }
          />
        </div>
      </div>

      {/* Opened over the start dialog rather than instead of it: the operator
          came here to start a timer, and once a card is attached they can press
          Start without finding their way back. `Dialog`'s stack closes the
          innermost one on Escape, so backing out lands here.

          It is the RESOURCE's own form because that is where `pricingListId`
          lives — and the picker inside it can create a first card without
          leaving, which is the dead end this whole change removes. */}
      {attaching && (
        <ResourceFormDialog
          open
          record={resource}
          onClose={() => setAttaching(false)}
          onSaved={() => setRefusal(null)}
        />
      )}
    </Dialog>
  );
}

const QUICK_MINUTES = [15, 30, 60];

export function ExtendSessionDialog({
  session,
  open,
  onClose,
  onDone,
}: Omit<Common, 'currency'> & { session: SessionRow | null }) {
  const { t } = useTranslation();
  const toastRefusal = useRefusalToast();
  const [minutes, setMinutes] = useState('30');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!session) return;
    setBusy(true);
    try {
      await api.post(`/resource-sessions/${session.id}/extend`, {
        minutes: Math.max(1, Math.round(Number(minutes) || 0)),
      });
      toast.success(t('spaces.resources.extended'));
      onDone();
      onClose();
    } catch (error) {
      toastRefusal(error, { resource: session.resource?.name });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('spaces.resources.extendTitle', { name: session?.resource?.name ?? '' })}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void submit()} disabled={busy}>
            {t('resource.extend')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex gap-2">
          {QUICK_MINUTES.map((value) => (
            <Button
              key={value}
              size="sm"
              variant={String(value) === minutes ? 'default' : 'outline'}
              onClick={() => setMinutes(String(value))}
            >
              +{value}
            </Button>
          ))}
        </div>
        <Field label={t('spaces.resources.extendMinutes')}>
          <Input
            type="number"
            min={1}
            value={minutes}
            onChange={(event) => setMinutes(event.currentTarget.value)}
          />
        </Field>
      </div>
    </Dialog>
  );
}

/** Stopping posts the TIME charge (P10-07), so the final meter is shown first. */
export function StopSessionDialog({
  session,
  open,
  currency,
  onClose,
  onDone,
}: Common & { session: SessionRow | null }) {
  const { t, i18n } = useTranslation();
  const toastRefusal = useRefusalToast();
  const [busy, setBusy] = useState(false);
  const meter = session ? meterFor(session, new Date()) : null;

  const submit = async () => {
    if (!session) return;
    setBusy(true);
    try {
      await api.post(`/resource-sessions/${session.id}/stop`, {});
      toast.success(t('spaces.resources.stopped'));
      onDone();
      onClose();
    } catch (error) {
      toastRefusal(error, { resource: session.resource?.name });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('spaces.resources.stopTitle', { name: session?.resource?.name ?? '' })}
      description={t('spaces.resources.stopConfirm')}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" onClick={() => void submit()} disabled={busy}>
            {t('resource.stop')}
          </Button>
        </>
      }
    >
      {meter && (
        <dl className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <dt className="text-xs text-ink-muted">{t('resource.elapsed')}</dt>
            <dd className="tabular-nums" dir="ltr">
              {formatDuration(meter.seconds)}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-ink-muted">
              {t('spaces.resources.finalAmount')}
            </dt>
            <dd className="font-semibold">
              {meter.amount === null
                ? t('spaces.resources.noPricing')
                : formatCurrency(meter.amount, currency, i18n.language)}
            </dd>
          </div>
          {meter.entitlement && (
            <>
              <div>
                <dt className="text-xs text-ink-muted">
                  {t('spaces.resources.entitlement.covered')}
                </dt>
                <dd className="flex items-center gap-2 tabular-nums" dir="ltr">
                  {formatDuration(meter.coveredSeconds)}
                  <Badge tone="brand">
                    {meter.entitlement.packageName ?? t('spaces.resources.entitlement.aPackage')}
                  </Badge>
                </dd>
              </div>
              <div>
                <dt className="text-xs text-ink-muted">
                  {t('spaces.resources.entitlement.billable')}
                </dt>
                <dd className="tabular-nums" dir="ltr">
                  {formatDuration(meter.billableSeconds)}
                </dd>
              </div>
            </>
          )}
          {meter.ruleName && (
            <div className="col-span-2">
              <dt className="text-xs text-ink-muted">
                {t('spaces.resources.rule')}
              </dt>
              <dd>{meter.ruleName}</dd>
            </div>
          )}
        </dl>
      )}
    </Dialog>
  );
}
